import React, { useCallback, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { CREATE_ACCOUNT_PATH, IS_WHOP } from "common/constants";
import { useNoExchangeConnectedModal } from "@shared/features/portfolio/hooks";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { WorkspaceType } from "@shared/types";
import useWorkspace from "utils/hooks/useWorkspace.ts";
import WhopCreateAccountModal from "features/whop/WhopCreateAccountModal.tsx";
import {
  AccountSetupProperties,
  markConnectOrigin,
  NoAccountPlacement,
  useAccountSetupAnalytics,
} from "analytics/accountSetup";

interface UseNoExchangeConnectedActionsProps {
  placement: NoAccountPlacement;
  action?: string;
  redirectOnClick?: boolean;
  onClose?: () => void;
}

const useNoExchangeConnectedActions = ({
  placement,
  action,
  redirectOnClick = true,
  onClose,
}: UseNoExchangeConnectedActionsProps) => {
  const history = useHistory();
  const displayData = useNoExchangeConnectedModal({ action });
  const { openCreateAccountDrawer } = useContext(AccountFormContext)!;
  const { onWorkspaceChange } = useWorkspace();
  const [whopModalOpen, setWhopModalOpen] = useState(false);
  const track = useAccountSetupAnalytics();
  const workspace = displayData.isDemo ? "demo" : "live";

  const trackViewed = useCallback(
    () => track("No Account Prompt - Viewed", { placement, workspace }),
    [track, placement, workspace]
  );

  const trackChoice = (choice: AccountSetupProperties["choice"]) =>
    track("No Account Prompt - Choice", { placement, workspace, choice });

  const onCreateClick = (isSwitchWorkspace?: boolean) => {
    trackChoice(isSwitchWorkspace ? "switch_workspace" : "connect_exchange");
    markConnectOrigin(placement);
    onClose?.();
    if (
      IS_WHOP &&
      ((isSwitchWorkspace && displayData.isDemo) ||
        (!isSwitchWorkspace && !displayData.isDemo))
    ) {
      setWhopModalOpen(true);
    } else if (redirectOnClick) {
      history.push(CREATE_ACCOUNT_PATH);
    } else {
      openCreateAccountDrawer();
    }
  };

  const onSwitchWorkspaceCreateClick = () => {
    onWorkspaceChange(
      displayData.isDemo ? WorkspaceType.USER : WorkspaceType.DEMO
    );
    onCreateClick(true);
  };

  const whopModal = IS_WHOP ? (
    <WhopCreateAccountModal
      open={whopModalOpen}
      onClose={() => setWhopModalOpen(false)}
    />
  ) : null;

  return {
    displayData,
    trackViewed,
    trackChoice,
    onCreateClick,
    onSwitchWorkspaceCreateClick,
    whopModal,
  };
};

export default useNoExchangeConnectedActions;
